<template>
    <div class="table-container w-full">
        <div class="list-top-container">
            <div class="list-search-container w-full mb-2 md:w-auto md:mb-0">
                <a-input-search
                    data-cy="migration-activities-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
        </div>

        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="migration-activities-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'status'">
                            <a-tag v-if="text == 'pending'" color="orange">
                                {{ $t('admin.migration.table.labels.pending') }}
                            </a-tag>
                            <a-tag v-if="text == 'running'" color="blue">
                                {{ $t('admin.migration.table.labels.running') }}
                            </a-tag>
                            <a-tag v-if="text == 'completed'" color="green">
                                {{ $t('admin.migration.table.labels.completed') }}
                            </a-tag>
                            <a-tag v-if="text == 'failed'" color="red">
                                {{ $t('admin.migration.table.labels.failed') }}
                            </a-tag>
                            <a-tag v-if="text == 'stopped'" color="yellow">
                                {{ $t('admin.migration.table.labels.stopped') }}
                            </a-tag>
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            data: [],
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
            checking_activities: {},
        };
    },
    props: {
        migration_id: {
            type: Number,
            default: 0,
        },
        current_migration_status: {
            type: String,
            default: 'pending',
        },
    },
    mounted() {
        this.startCycle();
    },
    beforeDestroy() {
        if (['pending', 'running'].includes(this.current_migration_status)) {
            clearInterval(this.checking_activities);
        }
    },
    computed: {
        getColumns() {
            return [
                {
                    title: this.$t('globals.tables.migration_activities.headers.table'),
                    key: 'table',
                    dataIndex: 'table',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.imported'),
                    key: 'imported',
                    dataIndex: 'imported',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.skipped'),
                    key: 'skipped',
                    dataIndex: 'skipped',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.processed'),
                    key: 'processed',
                    dataIndex: 'processed',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.records'),
                    key: 'records',
                    dataIndex: 'records',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.limit'),
                    key: 'limit',
                    dataIndex: 'limit',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.offset'),
                    key: 'offset',
                    dataIndex: 'offset',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.peak_memory'),
                    key: 'peak_memory',
                    dataIndex: 'peak_memory',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.memory'),
                    key: 'memory',
                    dataIndex: 'memory',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.elapsed_time'),
                    key: 'elapsed_time',
                    dataIndex: 'elapsed_time',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.time_remaining'),
                    key: 'time_remaining',
                    dataIndex: 'time_remaining',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.rate'),
                    key: 'rate',
                    dataIndex: 'rate',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.progress'),
                    key: 'progress',
                    dataIndex: 'progress',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration_activities.headers.status'),
                    key: 'status',
                    dataIndex: 'status',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
    },
    methods: {
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                this.search_value = input;

                this.fetch({
                    search_value: this.search_value,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        startCycle() {
            this.fetch();

            if (['pending', 'running'].includes(this.current_migration_status)) {
                this.checking_activities = setInterval(() => {
                    this.reload();
                }, 30000);
            }
        },
        fetch(params = {}) {
            this.loading = true;
            this.params = params;

            this.$http
                .get('/api/migrations/' + this.migration_id + '/activities', {
                    params: {
                        ...params,
                    },
                })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVMAF01' }),
                        });
                    }
                });
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
    },
};
</script>
